<template>
  <div>
    <v-app-bar app flat class="glass-navbar px-4 px-md-8" height="84">
      <!-- Mobile Icon -->
      <v-app-bar-nav-icon
        color="primary"
        class="hidden-md-and-up mr-2"
        @click.stop="$emit('toggle-drawer')"
      />

      <a
        v-if="isMobileHeader"
        :href="logoHref"
        class="mobile-brand text-decoration-none d-flex align-center mr-3"
      >
        <v-img
          :src="brand.logoUrl"
          max-height="30"
          max-width="30"
          contain
          class="mr-2"
          :alt="brand.name"
        ></v-img>
        <span class="mobile-brand__text font-orbitron font-weight-black">
          {{ brandingName }}
        </span>
      </a>

      <!-- Branding (desktop only) -->
      <a
        v-if="!isMobileHeader"
        :href="logoHref"
        class="navbar-brand text-decoration-none d-flex align-center mr-8 glhf-brand-link"
        style="gap: 18px;"
      >
        <img
          :src="brand.logoUrl"
          class="glhf-brand-logo"
          :alt="brand.name"
          @error="$event.target.src = brand.defaultLogo"
        />

        <span class="glhf-brand-copy">
          <!-- Word 1 without a color inherits; later uncolored words are spans so the accent rule
               (".glhf-brand-copy strong span") still applies; explicit colors are <b>. -->
          <strong class="font-orbitron">
            <template v-for="(word, i) in brand.words">
              <template v-if="i > 0">{{ " " }}</template>
              <b
                v-if="word.color"
                :key="'brand-word-' + i"
                :style="{ color: word.color, fontWeight: 'inherit' }"
                >{{ word.text }}</b
              >
              <template v-else-if="i === 0">{{ word.text }}</template>
              <span v-else :key="'brand-word-' + i">{{ word.text }}</span>
            </template>
          </strong>
          <small>Match Management</small>
        </span>
      </a>

      <!-- Desktop Nav Links -->
      <div v-if="!isMobileHeader" class="d-flex align-center flex-grow-1">
        <template v-for="(group, gIdx) in navLinks">
          <!-- Multi-item Dropdown -->
          <v-menu
            v-if="group.items && group.items.length > 0"
            :key="'group-' + gIdx"
            offset-y
            open-on-hover
            transition="slide-y-transition"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                text
                v-bind="attrs"
                v-on="on"
                class="font-weight-bold text-uppercase letter-spacing-1 mr-2 px-4 nav-btn"
                :class="{ 'nav-link-active': isGroupActive(group) }"
              >
                {{ group.title }}
                <v-icon right small>mdi-chevron-down</v-icon>
              </v-btn>
            </template>
            <v-list class="glass-card mt-2 py-0" min-width="200">
              <v-list-item
                v-for="(item, iIdx) in group.items"
                :key="'item-' + gIdx + '-' + iIdx"
                :to="item.to"
                exact
                active-class="primary--text"
              >
                <v-list-item-icon class="mr-3">
                  <v-icon small color="primary">{{ item.icon }}</v-icon>
                </v-list-item-icon>
                <v-list-item-title
                  class="font-weight-bold text-uppercase body-2"
                >
                  {{ item.title }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <!-- Simple Link. A group carrying `href` instead of `to` is an EXTERNAL app (the
               Dashboard), not a Vue route - passing an absolute URL to `to` would have the
               router try to match it as a path and fail. -->
          <v-btn
            v-else
            :key="'link-' + gIdx"
            :to="group.href ? undefined : group.to"
            :href="group.href"
            :target="group.href ? '_blank' : undefined"
            :rel="group.href ? 'noopener noreferrer' : undefined"
            text
            exact
            active-class="nav-link-active"
            class="font-weight-bold text-uppercase letter-spacing-1 mr-2 px-4 nav-btn"
          >
            {{ group.title }}
          </v-btn>
        </template>
      </div>

      <v-spacer v-if="!isMobileHeader"></v-spacer>

      <!-- User Zone -->
      <div v-if="!isMobileHeader" class="navbar-user-zone d-flex align-center">
        <v-btn
          v-if="isLocalGuestModeAvailable"
          depressed
          outlined
          :color="guestModeEnabled ? 'secondary' : 'primary'"
          class="font-weight-bold px-4 mr-3"
          rounded
          @click="toggleGuestMode"
        >
          <v-icon left size="18">{{
            guestModeEnabled ? "mdi-login-variant" : "mdi-incognito"
          }}</v-icon>
          {{ guestModeEnabled ? "Exit Guest Mode" : "Guest Mode" }}
        </v-btn>

        <v-chip
          v-if="guestModeEnabled"
          outlined
          color="secondary"
          class="mr-2 font-weight-bold"
          label
        >
          Local guest browsing
        </v-chip>

        <template v-else-if="user.id == null || user.id == -1">
          <v-btn
            depressed
            color="primary"
            class="black--text px-6 font-weight-bold neon-btn"
            rounded
            :href="apiUrl + '/auth/steam'"
          >
            <v-icon left size="20">mdi-steam</v-icon>
            Sign in via Steam
          </v-btn>
        </template>

        <template v-else>
          <v-menu offset-y bottom left transition="slide-y-transition">
            <template v-slot:activator="{ on, attrs }">
              <div
                v-bind="attrs"
                v-on="on"
                class="glhf-user-badge"
                role="button"
                tabindex="0"
              >
                <strong>{{ user.name }}</strong>
                <v-avatar size="34" class="glhf-user-avatar">
                  <v-img :src="user.small_image" />
                </v-avatar>
              </div>
            </template>
            <v-list class="glass-card mt-2 py-0" min-width="200">
              <v-list-item :to="'/user/' + user.id">
                <v-list-item-icon>
                  <v-icon color="primary">mdi-account-circle</v-icon>
                </v-list-item-icon>
                <v-list-item-title class="font-weight-bold">
                  {{ user.name }}
                </v-list-item-title>
              </v-list-item>
              <v-divider class="mx-2 opacity-10" />
              <v-list-item :href="apiUrl + '/logout'">
                <v-list-item-icon>
                  <v-icon color="cyber-pink">mdi-logout-variant</v-icon>
                </v-list-item-icon>
                <v-list-item-title class="cyber-pink--text font-weight-bold">
                  {{ $t("Navbar.Logout") }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </div>
    </v-app-bar>

    <ServerDialog
      v-model="newDialog"
      :serverInfo="{}"
      :title="$t('MyServers.New')"
    />
  </div>
</template>
<script>
import ServerDialog from "./ServerDialog";
import brand from "@/utils/brand";
export default {
  name: "Navbar",
  props: {
    user: Object,
    navLinks: {
      type: Array,
      default: () => []
    }
  },
  components: {
    ServerDialog
  },
  data() {
    return {
      newDialog: false,
      apiUrl: process.env?.VUE_APP_G5V_API_URL || "/api",
      dashboardUrl:
        process.env?.VUE_APP_DASHBOARD_URL || "https://dashboard.glhfparty.dk",
      guestModeEnabled: false,
      isLocalGuestModeAvailable: false
    };
  },
  computed: {
    isMobileHeader() {
      return this.$vuetify?.breakpoint?.smAndDown ?? false;
    },
    // Site-wide brand from the Dashboard (Settings -> Customization -> Branding), see utils/brand.js.
    brand() {
      return brand;
    },
    brandingName() {
      return brand.name;
    },
    // Only Super Admins get a Dashboard account they can actually manage things from -
    // everyone else (guests, regular admins, players) gets a non-link logo instead of a
    // dead/wrong-audience link.
    isDashboardLinked() {
      return this.user && this.user.super_admin == 1;
    },
    logoHref() {
      return this.isDashboardLinked ? this.dashboardUrl : null;
    }
  },
  methods: {
    readCookie(name) {
      const escapedName = name.replace(/([.*+?^${}()|[\]\\])/g, "\\$1");
      const match = document.cookie.match(
        new RegExp(`(?:^|; )${escapedName}=([^;]*)`)
      );
      return match ? decodeURIComponent(match[1]) : "";
    },
    syncGuestMode() {
      const host = window.location.hostname || "";
      this.isLocalGuestModeAvailable =
        host === "localhost" || host === "127.0.0.1" || host.startsWith("10.");
      this.guestModeEnabled = this.readCookie("g5v_local_mode") === "anonymous";
    },
    toggleGuestMode() {
      if (this.guestModeEnabled) {
        document.cookie = "g5v_local_mode=; Max-Age=0; Path=/; SameSite=Lax";
      } else {
        document.cookie =
          "g5v_local_mode=anonymous; Max-Age=31536000; Path=/; SameSite=Lax";
      }
      window.location.reload();
    },
    isGroupActive(group) {
      if (group.to && this.$route.path === group.to) return true;
      if (group.items) {
        return group.items.some(item => this.$route.path === item.to);
      }
      return false;
    }
  },
  mounted() {
    this.syncGuestMode();
  }
};
</script>

<style scoped>
.font-orbitron {
  font-family: "Orbitron", sans-serif !important;
}

.title-glow {
  text-shadow: 0 0 10px rgba(0, 242, 255, 0.5);
  font-size: 1.5rem !important;
  letter-spacing: 2px !important;
}

.letter-spacing-1 {
  letter-spacing: 1px !important;
}

.nav-btn {
  font-family: "Inter", sans-serif !important;
  font-size: 0.98rem !important;
  font-weight: 800 !important;
  letter-spacing: 0.08em !important;
  color: rgba(255, 255, 255, 0.76) !important;
  transition: all 0.3s ease;
  position: relative;
}

.nav-btn:hover {
  color: var(--neon-cyan) !important;
}

.nav-link-active {
  color: var(--neon-cyan) !important;
  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 20%;
    right: 20%;
    height: 2px;
    background: var(--neon-cyan);
    box-shadow: 0 0 10px var(--neon-cyan);
  }
}

.nav-create-btn {
  box-shadow: 0 0 15px rgba(0, 242, 255, 0.3) !important;
}

.neon-btn {
  box-shadow: 0 0 15px rgba(0, 242, 255, 0.3);
}

.neon-border {
  border: 2px solid var(--neon-cyan);
  box-shadow: 0 0 8px var(--neon-cyan);
}

.profile-avatar-btn:hover {
  transform: scale(1.05);
}

.opacity-10 {
  opacity: 0.1;
}

.glass-navbar {
  background: var(--glass-navbar-bg) !important;
  border-bottom: 1px solid var(--glass-border) !important;
}

/* GLHF top-bar parity (matches Dashboard/Panel): fills the 84px bar to within
   4px top and bottom */
.glhf-brand-logo {
  width: 90px;
  height: 76px;
  object-fit: contain;
  object-position: center;
  flex: 0 0 auto;
  filter: drop-shadow(0 0 10px rgba(244, 110, 0, 0.18));
}

.glhf-brand-copy {
  display: flex;
  flex-direction: column;
  justify-content: center;
  /* never wrap: with the 90px logo and the admin-only links the bar ran out of
     room and "GLHF Party" broke onto two lines */
  white-space: nowrap;
  flex: 0 0 auto;
}

.glhf-brand-copy strong {
  display: block;
  /* the brand name is admin-set (Dashboard Branding, up to 24 chars): never let it
     push the nav links off the bar */
  max-width: 30vw;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #ff7a00;
  font-size: clamp(1.6rem, 2.4vw, 2.2rem);
  font-weight: 800;
  line-height: 1;
  letter-spacing: 0.08em;
  text-shadow: 0 0 18px rgba(0, 228, 255, 0.18);
}

.glhf-brand-copy small {
  display: block;
  margin-top: 6px;
  color: rgba(255, 255, 255, 0.5);
  font-family: "Inter", sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
}

.glhf-user-badge {
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 56px;
  padding: 8px 14px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.03);
  cursor: pointer;
  transition: border-color 0.18s ease, background 0.18s ease;
}

.glhf-user-badge:hover {
  border-color: rgba(244, 110, 0, 0.4);
  background: rgba(244, 110, 0, 0.06);
}

.glhf-user-badge strong {
  color: #fff;
  font-family: "Orbitron", sans-serif;
  font-size: 1.2rem;
  letter-spacing: 0.08em;
  line-height: 1;
}

.glhf-user-avatar {
  border: 1px solid rgba(255, 255, 255, 0.08);
}

@media (max-width: 600px) {
  .glass-navbar {
    justify-content: flex-start;
    padding-left: 8px !important;
    padding-right: 8px !important;
  }

  .glass-navbar .v-app-bar__nav-icon {
    margin-right: 0 !important;
  }

  .mobile-brand {
    min-width: 0;
    max-width: calc(100vw - 88px);
  }

  .mobile-brand__text {
    color: var(--neon-cyan);
    font-size: 0.88rem;
    line-height: 1;
    letter-spacing: 0.08em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>

<style>
/* HUD design (Dashboard -> Settings -> General). App.vue sets html[data-glhf-design]
   from the Dashboard's /theme.json; only the navbar changes, the rest of G5V keeps its
   own design. Selectors are prefixed with the attribute so they outrank the scoped
   rules above, and !important where those rules use it. */
html[data-glhf-design="hud"] .glass-navbar {
  background: rgba(4, 6, 10, 0.92) !important;
  border-bottom: 1px solid rgba(92, 198, 255, 0.16) !important;
}

html[data-glhf-design="hud"] .glass-navbar .glhf-brand-logo {
  width: 64px;
  height: 56px;
  filter: drop-shadow(0 0 12px rgba(244, 110, 0, 0.25));
}

html[data-glhf-design="hud"] .glass-navbar .glhf-brand-copy strong {
  color: #eaf2ff;
  font-family: "Oxanium", sans-serif !important;
  font-size: 1.25rem;
  text-shadow: none;
}

html[data-glhf-design="hud"] .glass-navbar .glhf-brand-copy strong span {
  color: #f46e00;
}

html[data-glhf-design="hud"] .glass-navbar .glhf-brand-copy small {
  margin-top: 4px;
  color: #8494ae;
  font-family: "Oxanium", sans-serif;
  font-size: 0.68rem;
  letter-spacing: 0.3em;
}

html[data-glhf-design="hud"] .glass-navbar .nav-btn {
  border: 1px solid transparent;
  border-radius: 0 !important;
  color: #8494ae !important;
  font-family: "Oxanium", sans-serif !important;
  font-size: 0.8rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.16em !important;
}

html[data-glhf-design="hud"] .glass-navbar .nav-btn:hover,
html[data-glhf-design="hud"] .glass-navbar .nav-btn.nav-link-active {
  color: #f46e00 !important;
  border-color: rgba(244, 110, 0, 0.5);
  background: rgba(244, 110, 0, 0.08);
}

html[data-glhf-design="hud"] .glass-navbar .nav-btn::after {
  display: none;
}

html[data-glhf-design="hud"] .glass-navbar .glhf-user-badge {
  min-height: 44px;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
}

html[data-glhf-design="hud"] .glass-navbar .glhf-user-badge strong {
  font-family: "Oxanium", sans-serif;
  font-size: 1rem;
  letter-spacing: 0.02em;
}

html[data-glhf-design="hud"] .glass-navbar .glhf-user-avatar {
  border: 0 !important;
  border-radius: 0 !important;
  clip-path: polygon(25% 0, 75% 0, 100% 50%, 75% 100%, 25% 100%, 0 50%);
}

/* Navbar buttons (Sign in via Steam, Guest Mode): square, HUD font. The solid one gets
   the HUD's cut corners; outlined ones stay square so their border isn't cut away. */
html[data-glhf-design="hud"] .glass-navbar .navbar-user-zone .v-btn {
  border-radius: 0 !important;
  font-family: "Oxanium", sans-serif !important;
  letter-spacing: 0.12em !important;
}

html[data-glhf-design="hud"] .glass-navbar .navbar-user-zone .neon-btn {
  box-shadow: none !important;
  clip-path: polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px);
}

html[data-glhf-design="hud"] .glass-navbar .mobile-brand__text {
  font-family: "Oxanium", sans-serif !important;
}

/* Match the Dashboard/Panel brand inset. Vuetify adds 16px of .v-toolbar__content
   padding on top of the app-bar's px-md-8 (32px), pushing the logo to x=48.
   Zero it so the logo/brand start at 32px, identical to the Razor .topbar. */
.glass-navbar .v-toolbar__content {
  padding-left: 0 !important;
  padding-right: 0 !important;
}
</style>
